import React, {createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState} from "react";
import {AxiosError} from "axios";
import {TokenStorage} from "api/token";
import {setResponse} from "api/interceptors";
import {signout} from "features/login/api/authApi";
import {ConfirmCodeApiResponse} from "features/login/types";
import {isBrowser} from "shared/lib/fns";

const getInitialAuthenticated = () => {
  if (!isBrowser() || !window.Cypress) {
    return false;
  }
  
  return Boolean(window.initialState?.auth?.authenticated);
};

type AuthContextValue = {
  authenticated: boolean;
  isChecking: boolean;
  login: (tokens: ConfirmCodeApiResponse) => Promise<void>;
  logout: () => Promise<void>;
  clearSession: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const verifyToken = () => new Promise<void>((resolve, reject) => {
  TokenStorage.verifyToken(() => resolve(), reject);
});

export const AuthProvider = ({children}: {
  children: ReactNode
}) => {
  const [authenticated, setAuthenticated] = useState(getInitialAuthenticated);
  const [isChecking, setIsChecking] = useState(() => !getInitialAuthenticated());
  const initializedRef = useRef(false);
  
  const clearSession = useCallback(() => {
    TokenStorage.clear();
    setAuthenticated(false);
    setIsChecking(false);
  }, []);
  
  const applyAuthenticatedSession = useCallback(() => {
    setAuthenticated(true);
    setIsChecking(false);
  }, []);
  
  const login = useCallback(async (tokens: ConfirmCodeApiResponse) => {
    TokenStorage.storeToken(tokens.access);
    TokenStorage.storeRefreshToken(tokens.refresh);
    applyAuthenticatedSession();
  }, [applyAuthenticatedSession]);
  
  const logout = useCallback(async () => {
    const refresh = TokenStorage.getRefreshToken();
    
    try {
      if (refresh) {
        await new Promise<void>((resolve, reject) => {
          signout(refresh, resolve, reject as (error: AxiosError) => void);
        });
      }
    } finally {
      clearSession();
    }
  }, [clearSession]);
  
  useEffect(() => {
    setResponse(clearSession);
  }, [clearSession]);
  
  useEffect(() => {
    if (initializedRef.current) {
      return;
    }
    
    initializedRef.current = true;
    
    const accessToken = TokenStorage.getToken();
    
    if (!accessToken) {
      setIsChecking(false);
      return;
    }
    
    verifyToken()
    .then(applyAuthenticatedSession)
    .catch(() => {
      clearSession();
    });
  }, [applyAuthenticatedSession, clearSession]);
  
  return (<AuthContext.Provider value={{authenticated, isChecking, login, logout, clearSession}}>
      {children}
    </AuthContext.Provider>);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  
  return context;
};
